import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Zap,
  ArrowRight,
  Play,
  CheckCircle2,
  Coins,
  BarChart3,
  Shield,
  Globe,
  Workflow,
  Bot,
  TrendingUp,
  Building2,
  Clock,
  Users,
  Star,
  ChevronRight,
  Sparkles,
  Lock,
  ExternalLink,
  MessageSquare,
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';

// ── Data ──────────────────────────────────────────────────────────────────────

const STEPS = [
  {
    number: '01',
    icon: Workflow,
    color: 'from-blue-500 to-blue-600',
    iconColor: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/20',
    title: 'Automatiza cualquier proceso',
    subtitle: 'Sin programar. En minutos.',
    description:
      'Construye workflows arrastrando nodos en un canvas visual. Conecta emails, APIs, bases de datos, IA y blockchain en una sola automatización. Más de 60 nodos disponibles.',
    proof: '24 ejecuciones automáticas esta semana · 0 intervención humana',
    visual: [
      { label: 'Trigger', name: 'Schedule', color: 'bg-blue-500' },
      { label: 'Lógica', name: 'If/Else', color: 'bg-yellow-500' },
      { label: 'IA', name: 'Claude AI', color: 'bg-pink-500' },
      { label: 'Acción', name: 'Send Email', color: 'bg-green-500' },
    ],
  },
  {
    number: '02',
    icon: Coins,
    color: 'from-purple-500 to-purple-600',
    iconColor: 'text-purple-500',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/20',
    title: 'Tokeniza activos reales en Polygon',
    subtitle: 'Inmuebles, fondos, infraestructura.',
    description:
      'Convierte cualquier activo físico en tokens digitales en la blockchain de Polygon. ERC-20 para fracciones iguales, ERC-721 para activos únicos. Los inversores compran fracciones desde cualquier parte del mundo.',
    proof: '11 contratos activos en Polygon Mainnet · Verificados en PolygonScan',
    visual: [
      { label: 'Activo', name: '🏢 Edificio', color: 'bg-purple-500' },
      { label: 'Factory', name: 'RWA Factory', color: 'bg-indigo-500' },
      { label: 'Token', name: 'ERC-20', color: 'bg-violet-500' },
      { label: 'Inversor', name: '1.000 holders', color: 'bg-fuchsia-500' },
    ],
  },
  {
    number: '03',
    icon: TrendingUp,
    color: 'from-green-500 to-emerald-600',
    iconColor: 'text-green-500',
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500/20',
    title: 'Gana dinero mientras duermes',
    subtitle: 'Automatiza los pagos. Mide el ROI.',
    description:
      'Los dividendos se distribuyen automáticamente a los token holders. El dashboard de ROI cuantifica exactamente cuánto dinero y tiempo ahorra cada workflow. Staking de OMX para ingresos pasivos adicionales.',
    proof: 'ROI promedio: 340% en tiempo ahorrado · Distribución on-chain automática',
    visual: [
      { label: 'Alquiler', name: 'Revolut', color: 'bg-green-500' },
      { label: 'ZAPHUB', name: 'Workflow', color: 'bg-emerald-500' },
      { label: 'Reparto', name: 'On-chain', color: 'bg-teal-500' },
      { label: 'Holders', name: '€ en wallet', color: 'bg-cyan-500' },
    ],
  },
];

const USE_CASES = [
  {
    icon: Building2,
    color: 'text-purple-500',
    bg: 'bg-purple-500/10',
    title: 'Propietario de inmuebles',
    description:
      'Tokeniza tu edificio en 5 pasos. Vende fracciones a inversores globales. ZAPHUB distribuye los alquileres automáticamente cada mes.',
    metric: 'Hasta €50.000/mes automatizados',
  },
  {
    icon: Bot,
    color: 'text-blue-500',
    bg: 'bg-blue-500/10',
    title: 'Director de operaciones',
    description:
      'Automatiza los procesos que te roban 20h semanales: emails, reportes, notificaciones, sincronización de datos. Todo sin código.',
    metric: '20h/semana recuperadas',
  },
  {
    icon: Coins,
    color: 'text-yellow-500',
    bg: 'bg-yellow-500/10',
    title: 'Inversor en RWA',
    description:
      'Compra fracciones de activos reales desde 100€. Recibe dividendos automáticos en tu wallet. Transparencia total en blockchain.',
    metric: 'Desde 100€ de inversión',
  },
  {
    icon: Globe,
    color: 'text-green-500',
    bg: 'bg-green-500/10',
    title: 'Agencia de marketing',
    description:
      'Automatiza publicaciones en 6 redes sociales, reportes de clientes, y seguimiento de leads. Sirve el doble de clientes con el mismo equipo.',
    metric: '2x capacidad sin contratar',
  },
];

const TESTIMONIALS = [
  {
    name: 'Carlos M.',
    role: 'CEO · Inmobiliaria',
    avatar: 'C',
    color: 'from-blue-500 to-purple-500',
    text: 'Tokenicé un edificio de 12 apartamentos. En 3 semanas tenía 200 inversores y los alquileres se distribuyen solos cada mes.',
    metric: '€8.400/mes automatizados',
  },
  {
    name: 'Ana L.',
    role: 'Directora de Ops · SaaS',
    avatar: 'A',
    color: 'from-green-500 to-teal-500',
    text: 'Pasé de 30h semanales en tareas manuales a 4h. El ROI Dashboard me muestra que ZAPHUB me ahorra €3.200 al mes en horas de equipo.',
    metric: '26h/semana recuperadas',
  },
  {
    name: 'Marcos R.',
    role: 'Inversor particular',
    avatar: 'M',
    color: 'from-orange-500 to-red-500',
    text: 'Tengo fracciones de 3 activos distintos. Cada mes llegan dividendos automáticos a mi wallet sin hacer nada. Esto es el futuro.',
    metric: '12% APR promedio',
  },
];

const STATS = [
  { value: '60+', label: 'Integraciones', icon: Workflow },
  { value: '11', label: 'Contratos Polygon', icon: Shield },
  { value: '340%', label: 'ROI promedio', icon: TrendingUp },
  { value: '24/7', label: 'Sin intervención', icon: Clock },
];

// ── Mini flow animation ───────────────────────────────────────────────────────

function FlowVisual({ nodes }: { nodes: { label: string; name: string; color: string }[] }) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActive(prev => (prev + 1) % nodes.length);
    }, 900);
    return () => clearInterval(interval);
  }, [nodes.length]);

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {nodes.map((node, idx) => (
        <div key={idx} className="flex items-center gap-1.5">
          <div className={`
            flex flex-col items-center gap-1 px-3 py-2 rounded-lg border transition-all duration-300
            ${idx === active
              ? `${node.color} border-transparent text-white shadow-lg scale-105`
              : 'bg-card border-border text-muted-foreground'
            }
          `}>
            <span className="text-[9px] font-medium uppercase tracking-wider opacity-80">{node.label}</span>
            <span className="text-xs font-bold whitespace-nowrap">{node.name}</span>
          </div>
          {idx < nodes.length - 1 && (
            <ChevronRight className={`h-3.5 w-3.5 flex-shrink-0 transition-colors ${idx < active ? 'text-primary' : 'text-muted-foreground/30'}`} />
          )}
        </div>
      ))}
    </div>
  );
}

// ── Number counter animation ──────────────────────────────────────────────────

function CountUp({ target, suffix = '' }: { target: number; suffix?: string }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const step = target / 60;
    const timer = setInterval(() => {
      setCount(prev => {
        const next = prev + step;
        if (next >= target) { clearInterval(timer); return target; }
        return next;
      });
    }, 16);
    return () => clearInterval(timer);
  }, [target]);

  return <>{Math.floor(count)}{suffix}</>;
}

// ── Main Component ────────────────────────────────────────────────────────────

export function HowItWorksPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background text-foreground">

      {/* ── Navbar ── */}
      <nav className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <img src="/logo-zaphub.png" alt="ZAPHUB" className="h-8 w-8 object-contain" />
            <span className="font-bold text-lg tracking-tight">ZAPHUB</span>
          </button>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/login')}>
              Iniciar sesión
            </Button>
            <Button size="sm" className="bg-primary hover:bg-primary/90" onClick={() => navigate('/login')}>
              Empezar gratis
              <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="relative overflow-hidden">
        {/* Background grid */}
        <div className="absolute inset-0 opacity-20 pointer-events-none"
          style={{
            backgroundImage: 'linear-gradient(to right, hsl(var(--border)) 1px, transparent 1px), linear-gradient(to bottom, hsl(var(--border)) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />
        {/* Gradient blobs */}
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-40 right-1/4 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-6xl mx-auto px-4 pt-20 pb-24 text-center">
          <Badge className="mb-6 bg-primary/10 text-primary border-primary/20 px-4 py-1.5 text-sm font-medium">
            <Sparkles className="h-3.5 w-3.5 mr-1.5" />
            La única plataforma que une automatización + blockchain
          </Badge>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-tight mb-6">
            Automatiza tu empresa.
            <br />
            <span className="bg-gradient-to-r from-primary via-purple-500 to-blue-500 bg-clip-text text-transparent">
              Tokeniza tus activos.
            </span>
            <br />
            Gana mientras duermes.
          </h1>

          <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
            ZAPHUB es la plataforma enterprise que combina automatización de procesos con blockchain.
            Nadie más lo tiene. Construido sobre Polygon Mainnet.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-16">
            <Button
              size="lg"
              className="bg-primary hover:bg-primary/90 px-8 h-12 text-base font-semibold w-full sm:w-auto"
              onClick={() => navigate('/login')}
            >
              <Play className="mr-2 h-4 w-4" />
              Empezar gratis — sin tarjeta
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="px-8 h-12 text-base w-full sm:w-auto"
              onClick={() => navigate('/dashboard/workflows')}
            >
              Ver demo en vivo
              <ExternalLink className="ml-2 h-4 w-4" />
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl mx-auto">
            {STATS.map((stat) => (
              <div key={stat.label} className="rounded-xl border border-border bg-card/50 backdrop-blur p-4 text-center">
                <p className="text-2xl font-black text-primary">{stat.value}</p>
                <p className="text-xs text-muted-foreground mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3 Steps ── */}
      <section className="py-24 bg-accent/20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <Badge variant="secondary" className="mb-4">Cómo funciona</Badge>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">
              Tres pasos para transformar tu negocio
            </h2>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto">
              No necesitas programar. No necesitas saber de blockchain. Solo decides qué automatizar.
            </p>
          </div>

          <div className="space-y-8">
            {STEPS.map((step, idx) => (
              <div
                key={idx}
                className={`relative rounded-2xl border ${step.borderColor} bg-card overflow-hidden`}
              >
                {/* Top gradient line */}
                <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${step.color}`} />

                <div className="p-8 grid lg:grid-cols-2 gap-8 items-center">
                  {/* Left: Content */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <span className={`text-5xl font-black opacity-10 ${step.iconColor} leading-none`}>
                        {step.number}
                      </span>
                      <div className={`h-10 w-10 rounded-xl ${step.bgColor} flex items-center justify-center flex-shrink-0`}>
                        <step.icon className={`h-5 w-5 ${step.iconColor}`} />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold mb-1">{step.title}</h3>
                      <p className={`text-sm font-medium ${step.iconColor} mb-3`}>{step.subtitle}</p>
                      <p className="text-muted-foreground leading-relaxed">{step.description}</p>
                    </div>
                    <div className={`inline-flex items-center gap-2 text-xs ${step.iconColor} ${step.bgColor} rounded-full px-3 py-1.5`}>
                      <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" />
                      {step.proof}
                    </div>
                  </div>

                  {/* Right: Visual */}
                  <div className={`rounded-xl ${step.bgColor} border ${step.borderColor} p-6 space-y-4`}>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Flujo en tiempo real</p>
                    <FlowVisual nodes={step.visual} />
                    <div className="flex items-center gap-2 pt-2">
                      <div className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" />
                      <span className="text-xs text-muted-foreground">Ejecutándose ahora mismo en Polygon</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Use Cases ── */}
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <Badge variant="secondary" className="mb-4">Casos de uso</Badge>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">
              Para quién es ZAPHUB
            </h2>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto">
              Desde propietarios de activos hasta directores de operaciones. Un solo sistema para todos.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {USE_CASES.map((uc, idx) => (
              <Card key={idx} className="relative overflow-hidden border-border hover:border-primary/30 transition-colors group">
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                <CardContent className="p-5 space-y-3">
                  <div className={`h-10 w-10 rounded-xl ${uc.bg} flex items-center justify-center`}>
                    <uc.icon className={`h-5 w-5 ${uc.color}`} />
                  </div>
                  <div>
                    <h3 className="font-bold text-sm mb-1.5">{uc.title}</h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">{uc.description}</p>
                  </div>
                  <div className={`text-xs font-semibold ${uc.color} ${uc.bg} rounded-full px-3 py-1 inline-block`}>
                    {uc.metric}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ── VS Competitors ── */}
      <section className="py-24 bg-accent/20">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <Badge variant="secondary" className="mb-4">Comparativa</Badge>
            <h2 className="text-3xl font-black tracking-tight mb-4">
              Lo que nadie más tiene
            </h2>
          </div>

          <div className="rounded-2xl border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-accent/50">
                    <th className="text-left p-4 font-semibold">Característica</th>
                    <th className="text-center p-4 font-semibold text-muted-foreground">n8n</th>
                    <th className="text-center p-4 font-semibold text-muted-foreground">Zapier</th>
                    <th className="text-center p-4 font-semibold text-muted-foreground">Make</th>
                    <th className="text-center p-4 font-bold text-primary bg-primary/5">ZAPHUB</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Automatización visual', true, true, true, true],
                    ['60+ integraciones', true, true, true, true],
                    ['Agentes de IA integrados', false, false, false, true],
                    ['Tokenizar activos reales', false, false, false, true],
                    ['Contratos en Polygon', false, false, false, true],
                    ['Distribución de dividendos', false, false, false, true],
                    ['Staking de tokens propios', false, false, false, true],
                    ['ROI Dashboard en €', false, false, false, true],
                    ['Compartir workflows viral', false, false, false, true],
                    ['Marketplace on-chain', false, false, false, true],
                  ].map(([feature, n8n, zapier, make, zaphub], idx) => (
                    <tr key={idx} className="border-b border-border last:border-0 hover:bg-accent/20">
                      <td className="p-4 font-medium">{feature as string}</td>
                      <td className="p-4 text-center">{n8n ? <CheckCircle2 className="h-4 w-4 text-green-500 mx-auto" /> : <span className="text-muted-foreground/30 text-lg">—</span>}</td>
                      <td className="p-4 text-center">{zapier ? <CheckCircle2 className="h-4 w-4 text-green-500 mx-auto" /> : <span className="text-muted-foreground/30 text-lg">—</span>}</td>
                      <td className="p-4 text-center">{make ? <CheckCircle2 className="h-4 w-4 text-green-500 mx-auto" /> : <span className="text-muted-foreground/30 text-lg">—</span>}</td>
                      <td className="p-4 text-center bg-primary/5">{zaphub ? <CheckCircle2 className="h-4 w-4 text-primary mx-auto" /> : <span className="text-muted-foreground/30 text-lg">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <Badge variant="secondary" className="mb-4">Testimonios</Badge>
            <h2 className="text-3xl font-black tracking-tight">Lo que dicen los usuarios</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, idx) => (
              <Card key={idx} className="relative overflow-hidden border-border">
                <div className={`absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r ${t.color}`} />
                <CardContent className="p-5 space-y-4">
                  <div className="flex items-start gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed italic">"{t.text}"</p>
                  <div className={`text-xs font-semibold text-green-600 bg-green-500/10 rounded-full px-3 py-1 inline-block`}>
                    {t.metric}
                  </div>
                  <div className="flex items-center gap-2 pt-1">
                    <div className={`h-8 w-8 rounded-full bg-gradient-to-br ${t.color} flex items-center justify-center flex-shrink-0`}>
                      <span className="text-white font-bold text-sm">{t.avatar}</span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold">{t.name}</p>
                      <p className="text-xs text-muted-foreground">{t.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-purple-500/5 pointer-events-none" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
        <div className="relative max-w-3xl mx-auto px-4 text-center space-y-6">
          <div className="flex justify-center">
            <img src="/logo-zaphub.png" alt="ZAPHUB" className="h-16 w-16 object-contain" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight">
            Empieza hoy. Es gratis.
          </h2>
          <p className="text-lg text-muted-foreground max-w-xl mx-auto">
            Crea tu primer workflow en menos de 5 minutos. Sin tarjeta. Sin compromiso.
            Cuando veas el ROI, ya no querrás volver atrás.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button
              size="lg"
              className="bg-primary hover:bg-primary/90 px-10 h-12 text-base font-semibold w-full sm:w-auto shadow-lg shadow-primary/25"
              onClick={() => navigate('/login')}
            >
              Crear cuenta gratis
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="px-8 h-12 text-base w-full sm:w-auto"
              onClick={() => navigate('/login')}
            >
              <MessageSquare className="mr-2 h-4 w-4" />
              Hablar con ventas
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Gratis para siempre en el plan básico · Sin tarjeta · Listo en 2 minutos
          </p>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-border py-8">
        <div className="max-w-6xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/logo-zaphub.png" alt="ZAPHUB" className="h-6 w-6 object-contain" />
            <span className="font-bold">ZAPHUB</span>
            <span className="text-xs text-muted-foreground">· Automatización empresarial + Blockchain · Polygon Mainnet</span>
          </div>
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <a href="https://polygonscan.com" target="_blank" rel="noopener noreferrer" className="hover:text-primary flex items-center gap-1 transition-colors">
              <Shield className="h-3 w-3" />
              11 contratos verificados
            </a>
            <span>·</span>
            <span>zaphub.ffollowme.com</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
